import React, { useState, useEffect } from 'react';
import { Card, Row, Col, ListGroup, Badge, Spinner, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { API_URL, getDeviceId } from '../api';

interface ListSummary {
  id: string;
  name: string;
  item_count: number;
  unchecked_count: number;
  updated_at: string;
}

interface ReceiptSummary {
  id: number;
  chain_name: string | null;
  store_name: string | null;
  purchase_date: string | null;
  total_amount: number | null;
  status: string;
  line_count: number;
  matched_count: number;
}

const Home: React.FC = () => {
  const [lists, setLists] = useState<ListSummary[]>([]);
  const [receipts, setReceipts] = useState<ReceiptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const deviceId = getDeviceId();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = new URLSearchParams({ device_id: deviceId });
      const [listsRes, receiptsRes] = await Promise.all([
        fetch(`${API_URL}/shopping_lists/?${params}`).catch(() => null),
        fetch(`${API_URL}/receipts/?${params}`).catch(() => null),
      ]);
      if (listsRes?.ok) setLists(await listsRes.json());
      if (receiptsRes?.ok) setReceipts(await receiptsRes.json());
      setLoading(false);
    })();
  }, [deviceId]);

  const totalReceipts = receipts.length;
  const confirmedReceipts = receipts.filter(r => r.status === 'confirmed').length;
  const activeList = lists.length > 0 ? lists[0] : null;

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" size="sm" /> <span className="ms-2">Laden...</span>
      </div>
    );
  }

  return (
    <>
      <h4 className="mb-3">SmartCart</h4>
      <p className="text-muted mb-4">
        Sag uns was du brauchst &mdash; wir sagen dir wo, wie günstig und wie du am schnellsten hinkommst.
      </p>

      {/* Quick Actions */}
      <Row className="mb-4 g-3">
        <Col sm={6} md={3}>
          <Card as={Link} to="/shopping-lists" className="text-decoration-none h-100">
            <Card.Body className="text-center">
              <div className="fs-1 mb-2">&#128221;</div>
              <Card.Title className="fs-6">Einkaufsliste</Card.Title>
              {activeList && (
                <Badge bg="primary">{activeList.unchecked_count} offen</Badge>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={3}>
          <Card as={Link} to="/product-search" className="text-decoration-none h-100">
            <Card.Body className="text-center">
              <div className="fs-1 mb-2">&#128269;</div>
              <Card.Title className="fs-6">Produktmonitor</Card.Title>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={3}>
          <Card as={Link} to="/receipts" className="text-decoration-none h-100">
            <Card.Body className="text-center">
              <div className="fs-1 mb-2">&#129534;</div>
              <Card.Title className="fs-6">Bon hochladen</Card.Title>
              {totalReceipts > 0 && (
                <Badge bg="secondary">{totalReceipts} Bons</Badge>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={3}>
          <Card as={Link} to="/settings" className="text-decoration-none h-100">
            <Card.Body className="text-center">
              <div className="fs-1 mb-2">&#9881;</div>
              <Card.Title className="fs-6">Einstellungen</Card.Title>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        {/* Recent Lists */}
        <Col md={6}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Card.Title className="mb-0 fs-6">Meine Listen</Card.Title>
                <Link to="/shopping-lists" className="small">Alle anzeigen</Link>
              </div>
              {lists.length === 0 ? (
                <p className="text-muted small mb-0">Noch keine Listen. Erstelle deine erste Einkaufsliste!</p>
              ) : (
                <ListGroup variant="flush">
                  {lists.slice(0, 5).map(l => (
                    <ListGroup.Item key={l.id} as={Link} to="/shopping-lists" action
                      className="d-flex justify-content-between align-items-center px-0">
                      <span>{l.name}</span>
                      <Badge bg="secondary">{l.unchecked_count}/{l.item_count}</Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Receipt Stats */}
        <Col md={6}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Card.Title className="mb-0 fs-6">Kassenbons</Card.Title>
                <Link to="/receipts" className="small">Alle anzeigen</Link>
              </div>
              {totalReceipts === 0 ? (
                <p className="text-muted small mb-0">
                  Noch keine Bons. Lade deinen ersten Kassenbon hoch und hilf der Community!
                </p>
              ) : (
                <>
                  <div className="d-flex gap-3 mb-3">
                    <div className="text-center">
                      <div className="fs-3 fw-bold">{totalReceipts}</div>
                      <small className="text-muted">Hochgeladen</small>
                    </div>
                    <div className="text-center">
                      <div className="fs-3 fw-bold text-success">{confirmedReceipts}</div>
                      <small className="text-muted">Bestätigt</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-0">
                    Danke! Du hilfst anderen Nutzern, günstigere Preise zu finden.
                  </p>
                </>
              )}
              <div className="mt-2">
                <Button as={Link as any} to="/receipts" size="sm" variant="outline-primary">
                  Bon hochladen
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Home;
